import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, X, Monitor, Wrench, ShoppingCart } from 'lucide-react';
import fuzzy from 'fuzzy';
import './ChatBot.css';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      from: 'bot', 
      text: '👋 Welcome to PROPERTECK! I\'m here to help you with:\n\n🖥️ Computer & laptop recommendations\n🔧 Repair services & troubleshooting\n🛒 Product availability & pricing\n📞 Store hours & contact info\n\nHow can I assist you today?', 
      timestamp: new Date() 
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [products, setProducts] = useState([]);
  const messagesEndRef = useRef(null);
  
  const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  const GEMINI_API_KEY = 'AIzaSyD0Ua_ebhOFgICdn3norMnYzzLxBVfXdrM';
  // Use v1beta (the correct version) and pass API key in header
  const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${baseURL}/api/products/getproducts`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        let productArray = [];
        if (Array.isArray(data)) {
          productArray = data;
        } else if (Array.isArray(data.products)) {
          productArray = data.products;
        } else if (Array.isArray(data.data)) {
          productArray = data.data;
        } else {
          throw new Error('Response is not a valid product array');
        }
        setProducts(productArray);
      } catch (error) {
        console.error('Error fetching products:', error);
        setMessages(prev => [
          ...prev,
          { from: 'bot', text: '⚠️ Unable to fetch product data. Please try again later or contact us at 0779490055.', timestamp: new Date() }
        ]);
      }
    };
    fetchProducts();
  }, []);

  const generateShopContext = () => {
    const productList = Array.isArray(products) && products.length > 0 
      ? products.map(p => `- ${p.name}: LKR ${p.price} (${p.description}) [Category: ${p.category}]`).join('\n')
      : 'Product data is currently unavailable.';

    return `You are a helpful assistant for "PROPERTECK Computer Shop". Here's important information about our shop:

PRODUCTS & PRICES:
${productList}

SHOP DETAILS:
- Location: Giriulla
- Hotline: 0779490055
- Free diagnostics for repair services
- 30-day warranty on all repairs
- Price matching on competitor prices

SERVICES WE OFFER:
- Computer and laptop sales
- Hardware repairs and upgrades
- Software installation and troubleshooting
- Virus removal and malware cleanup
- Data backup and recovery
- Network setup and connectivity issues
- Performance optimization

INSTRUCTIONS:
- Always be helpful, professional, and friendly
- Focus on solving customer problems
- Recommend products from our inventory when relevant
- For technical issues, suggest bringing the device to the store for proper diagnosis
- Include relevant emojis to make responses more engaging
- Keep responses concise but informative
- If asked about products not in our inventory, suggest similar alternatives using fuzzy matching
- Always mention our key services: free diagnostics, 30-day warranty, price matching
- When a user asks about a product that doesn't exactly match, suggest the closest matching product from the list using fuzzy matching
- If no close match is found, respond with: "I couldn't find that exact product, but here are similar items available at PROPERTECK: [list similar products]"
- Always provide the hotline number 0779490055 when users need direct assistance
- Do not display email addresses in responses

Respond as a knowledgeable computer shop assistant who wants to help customers find the right products and solutions.`;
  };

  const quickActions = [
    { icon: Monitor, text: "View Products", action: "I'd like to see your available computers and laptops" },
    { icon: Wrench, text: "Repair Services", action: "I need help with computer repair services" },
    { icon: ShoppingCart, text: "Store Info", action: "What are your store hours and location?" }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleChat = () => {
    setIsOpen(true);
  };

  const closeChat = () => {
    setIsOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const findSimilarProducts = (query) => {
    if (!query || !Array.isArray(products) || products.length === 0) {
      console.log('No valid products for fuzzy matching:', products);
      return [];
    }
    const results = fuzzy.filter(query, products.map(p => p.name), { extract: p => p });
    return results.map(r => r.original).slice(0, 3);
  };

  const callGeminiAPI = async (userMessage, conversationHistory = []) => {
    try {
      const similarProducts = findSimilarProducts(userMessage);
      let contextPrompt = generateShopContext() + "\n\nConversation history:\n";
      
      const recentMessages = conversationHistory.slice(-6);
      recentMessages.forEach(msg => {
        contextPrompt += `${msg.from === 'user' ? 'Customer' : 'Assistant'}: ${msg.text}\n`;
      });

      if (similarProducts.length > 0) {
        contextPrompt += `\nThe user asked about "${userMessage}". The closest matching products are:\n${similarProducts.map(p => `- ${p.name}: LKR ${p.price} (${p.description})`).join('\n')}\n`;
      }

      contextPrompt += `\nCustomer: ${userMessage}\nAssistant:`;

      const response = await fetch(GEMINI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': GEMINI_API_KEY,  // Pass API key in header
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: contextPrompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            maxOutputTokens: 500,
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Gemini API error details:', errorData);
        throw new Error(`Gemini API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text;
      } else {
        throw new Error('Invalid response format from Gemini API');
      }
    } catch (error) {
      console.error('Gemini API Error:', error);
      
      // Improved error handling with specific messages
      if (error.message.includes('404')) {
        return '⚠️ I\'m currently experiencing technical difficulties connecting to my AI service.\n\n📞 For immediate assistance, please contact us at:\n\n**Hotline: 0779490055**\n📍 Location: Giriulla\n\nOur team is ready to help you with any questions about computers, repairs, or our services!';
      } else if (error.message.includes('429')) {
        return '⚠️ I\'m receiving too many requests right now. Please try again in a moment.\n\n📞 For immediate assistance, call us at **0779490055**';
      } else if (error.message.includes('403')) {
        return '⚠️ There\'s an authentication issue with the AI service.\n\n📞 Please contact us directly at **0779490055** for assistance!';
      } else {
        return '⚠️ I apologize, but I\'m having trouble processing your request right now.\n\nHowever, I can still help you! Here\'s how to reach us:\n\n📞 **Hotline: 0779490055**\n📍 Location: Giriulla\n\n🏪 We offer:\n• Computer & laptop sales\n• Hardware repairs & upgrades\n• Software troubleshooting\n• Free diagnostics\n• 30-day warranty on repairs\n\nCall us now for immediate assistance!';
      }
    }
  };

  const sendMessage = async (messageText = null) => {
    const messageToSend = messageText || input.trim();
    if (messageToSend === '') return;

    const userMessage = { 
      from: 'user', 
      text: messageToSend, 
      timestamp: new Date() 
    };
    setMessages(prev => [...prev, userMessage]);
    
    if (!messageText) setInput('');
    setIsTyping(true);

    const botResponse = await callGeminiAPI(messageToSend, messages);
    
    setMessages(prev => [
      ...prev,
      { from: 'bot', text: botResponse, timestamp: new Date() }
    ]);
    setIsTyping(false);
  };

  const handleQuickAction = (actionText) => {
    sendMessage(actionText);
  };

  return (
    <div className="chatbot-container">
      {isOpen && <div className="chatbot-backdrop"></div>}
      <div 
        className="chatbot-toggle"
        onClick={toggleChat}
      >
        <MessageCircle className="chatbot-icon" />
      </div>

      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="shop-logo">
              <Monitor className="shop-logo-icon" />
            </div>
            <div className="shop-info">
              <div className="shop-name">PROPERTECK Support</div>
              <div className="shop-status">
                <span className="chat-status-indicator"></span>
                AI-Powered • Ready to help!
              </div>
            </div>
            <button onClick={closeChat} className="close-button">
              <X className="close-icon" />
            </button>
          </div>

          {messages.length <= 1 && (
            <div className="quick-actions-section">
              <div className="quick-actions-title">Quick actions:</div>
              <div className="quick-actions-buttons">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(action.action)}
                    className="quick-action-button"
                  >
                    <action.icon className="quick-action-icon" />
                    {action.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index} className={`message-wrapper message-wrapper-${msg.from}`}>
                <div className={`message message-${msg.from}`}>
                  <div className="message-text">{msg.text}</div>
                  <div className="message-timestamp">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="message-wrapper message-wrapper-bot">
                <div className="typing-indicator">
                  <div className="typing-dots">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="typing-dot" />
                    ))}
                  </div>
                  <span className="typing-text">PROPERTECK is typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input">
            <div className="chat-input-group">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about computers, repairs, or store info..."
                disabled={isTyping}
                className="chat-input-field"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || isTyping}
                className={`chat-send-button ${input.trim() && !isTyping ? 'chat-send-button-active' : 'chat-send-button-disabled'}`}
              >
                <Send className="send-icon" />
              </button>
            </div>
            <div className="footer-text">
              💬 AI-powered assistance • Call 0779490055 for immediate help
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;